// Sky-coverage symbology rendered as map images. Quartile-based
// rendering matching boss's mental model (2026-05-10):
//
//   CLR / SKC   open circle  (0%)
//   FEW         25% filled
//   SCT         50% filled
//   BKN         75% filled
//   OVC         100% filled
//   OVX         filled circle with diagonal slash (sky obscured)
//
// Note: traditional WMO synoptic symbols use 1/8, 2/8, 3/8 ... 8/8
// fractions (FEW ≈ 12.5%). The quartile rendering is more legible at
// the small icon sizes we use on the EFB and matches how pilots
// commonly think about METAR cloud-cover codes.
//
// One icon per code; the layer picks via a `match` expression on the
// feature's `skyCover` property.

import type { Map as MlMap, ExpressionSpecification } from 'maplibre-gl';
import { ensureSvgImages } from './iconRegistry';

const SIZE = 24;
const CX = SIZE / 2;
const CY = SIZE / 2;
const R  = 8;

const STROKE = '#0f172a';
const FILL   = '#f8fafc';     // matches the ceilvis marker color
const FG     = '#0f172a';     // dark slice fill

function svgWedge(fractionFilled: number): string {
  // Render a circle with a sector of fill spanning `fractionFilled`
  // (0..1) clockwise from 12 o'clock. Use the standard 'arc' SVG path.
  const angle = 2 * Math.PI * fractionFilled;
  // Path tip at top of circle (12 o'clock) -> arc to angle.
  const x1 = CX + R * Math.sin(angle);
  const y1 = CY - R * Math.cos(angle);
  const largeArc = fractionFilled > 0.5 ? 1 : 0;
  const slice = fractionFilled <= 0
    ? ''
    : fractionFilled >= 1
      ? `<circle cx="${CX}" cy="${CY}" r="${R}" fill="${FG}"/>`
      : `<path d="M ${CX} ${CY} L ${CX} ${CY - R} A ${R} ${R} 0 ${largeArc} 1 ${x1.toFixed(2)} ${y1.toFixed(2)} Z" fill="${FG}"/>`;
  return `
    <svg xmlns="http://www.w3.org/2000/svg" width="${SIZE}" height="${SIZE}" viewBox="0 0 ${SIZE} ${SIZE}">
      <circle cx="${CX}" cy="${CY}" r="${R}" fill="${FILL}" stroke="${STROKE}" stroke-width="1.4"/>
      ${slice}
    </svg>
  `;
}

function svgObscured(): string {
  // OVX \u2014 vertical visibility obscured. Black filled circle with a
  // diagonal slash (matches WMO synoptic symbol \"sky obscured\").
  return `
    <svg xmlns="http://www.w3.org/2000/svg" width="${SIZE}" height="${SIZE}" viewBox="0 0 ${SIZE} ${SIZE}">
      <circle cx="${CX}" cy="${CY}" r="${R}" fill="${FG}" stroke="${STROKE}" stroke-width="1.4"/>
      <line x1="${CX - R * 0.7}" y1="${CY - R * 0.7}" x2="${CX + R * 0.7}" y2="${CY + R * 0.7}" stroke="${FILL}" stroke-width="2.2" stroke-linecap="round"/>
    </svg>
  `;
}

const SYMBOLS: Array<{ id: string; svg: string }> = [
  { id: 'sky-CLR', svg: svgWedge(0) },
  { id: 'sky-SKC', svg: svgWedge(0) },
  { id: 'sky-FEW', svg: svgWedge(0.25) }, // 1/4
  { id: 'sky-SCT', svg: svgWedge(0.50) }, // 2/4
  { id: 'sky-BKN', svg: svgWedge(0.75) }, // 3/4
  { id: 'sky-OVC', svg: svgWedge(1) },    // 4/4
  { id: 'sky-OVX', svg: svgObscured() },
  { id: 'sky-UNKN', svg: svgWedge(0) },
];

export const SKY_LEGEND_SVGS = {
  CLR: svgWedge(0),
  FEW: svgWedge(0.25),
  SCT: svgWedge(0.50),
  BKN: svgWedge(0.75),
  OVC: svgWedge(1),
  OVX: svgObscured(),
};

export function ensureSkyCoverIcons(map: MlMap): Promise<void> {
  return ensureSvgImages(map, SYMBOLS, {
    defaultSize: SIZE,
    logTag: '[skyCoverIcons]',
  });
}

/** Pick the right sky-cover icon id for a feature. */
export const SKY_COVER_ICON_EXPRESSION: ExpressionSpecification = [
  'concat',
  'sky-',
  ['coalesce', ['get', 'skyCover'], 'UNKN'],
] as unknown as ExpressionSpecification;
